import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/DataTable";
import { ShieldCheck, Banknote, CreditCard, Wrench, Check, Minus } from "lucide-react";

type Role = {
  key: string;
  name: string;
  badge: "primary" | "info" | "warning" | "success";
  icon: React.ReactNode;
  short: string;
  description: string;
  permissions: string[];
};

const ROLES: Role[] = [
  {
    key: "merchant_admin",
    name: "Admin marchand",
    badge: "primary",
    icon: <ShieldCheck size={20} />,
    short: "Administrateur global du compte marchand",
    description:
      "Gère l'équipe (invitation, rôles, suspension, suppression), les clés API, les paramètres et tous les modules. Premier compte créé automatiquement lors de l'onboarding par Moko.",
    permissions: [
      "Voir et gérer tous les paiements",
      "Voir tous les wallets, statements, historique, settlements",
      "Initier des remboursements",
      "Inviter et gérer l'équipe (rôles, suspension, suppression)",
      "Modifier le logo, la callback URL et les informations marchand",
    ],
  },
  {
    key: "finance",
    name: "Finance",
    badge: "info",
    icon: <Banknote size={20} />,
    short: "Comptabilité et trésorerie",
    description:
      "Accès complet aux flux financiers et opérations de remboursement, sans pouvoir gérer l'équipe ni les paramètres techniques.",
    permissions: [
      "Voir tous les paiements",
      "Voir les wallets, statements, historique, settlements",
      "Initier des remboursements",
      "Voir le funnel et les statistiques",
      "Consulter l'équipe (lecture seule)",
    ],
  },
  {
    key: "commercial",
    name: "Commercial",
    badge: "success",
    icon: <CreditCard size={20} />,
    short: "Service client et suivi des paiements",
    description:
      "Recherche les paiements par référence ou numéro de billet pour répondre aux demandes des passagers. Pas d'accès aux wallets, refunds, ni aux paramètres techniques.",
    permissions: [
      "Voir tous les paiements",
      "Rechercher par numéro de billet (parsed_metadata)",
      "Voir le funnel et les statistiques",
      "Consulter l'équipe (lecture seule)",
    ],
  },
  {
    key: "it",
    name: "Informatique",
    badge: "warning",
    icon: <Wrench size={20} />,
    short: "Intégration technique et debug — lecture seule sur les flux financiers",
    description:
      "Diagnostique les problèmes d'intégration (webhooks, callbacks, clés API). Voit toutes les transactions (paiements + funnel) pour le debug. Ne peut JAMAIS initier un remboursement — c'est une opération financière réservée aux rôles finance/merchant_admin.",
    permissions: [
      "Voir tous les paiements (lecture)",
      "Voir le funnel et les statistiques",
      "Voir et replay les webhooks sortants",
      "Voir et régénérer les clés API (propriétaire du cycle de vie)",
      "Renvoyer un callback manuellement",
      "Consulter l'équipe (lecture seule)",
    ],
  },
];

const MATRIX_FEATURES: { label: string; allowed: Record<string, boolean> }[] = [
  { label: "Voir les paiements", allowed: { merchant_admin: true, finance: true, commercial: true, it: true } },
  { label: "Initier un refund", allowed: { merchant_admin: true, finance: true, commercial: false, it: false } },
  { label: "Voir funnel & stats", allowed: { merchant_admin: true, finance: true, commercial: true, it: true } },
  { label: "Voir wallets / statements", allowed: { merchant_admin: true, finance: true, commercial: false, it: false } },
  { label: "Voir webhooks", allowed: { merchant_admin: false, finance: false, commercial: false, it: true } },
  { label: "Renvoyer un callback", allowed: { merchant_admin: false, finance: false, commercial: false, it: true } },
  { label: "Voir clés API", allowed: { merchant_admin: false, finance: false, commercial: false, it: true } },
  { label: "Régénérer clés API", allowed: { merchant_admin: false, finance: false, commercial: false, it: true } },
  { label: "Inviter / supprimer membres", allowed: { merchant_admin: true, finance: false, commercial: false, it: false } },
  { label: "Modifier paramètres marchand", allowed: { merchant_admin: true, finance: false, commercial: false, it: false } },
];

export function UserRolesPage() {
  return (
    <div>
      <PageHeader
        title="Rôles et permissions"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Rôles" }]}
      />

      <div className="card mb-6">
        <div className="card-body text-sm text-[#3f4254] leading-relaxed">
          Chaque membre de votre équipe a un rôle qui détermine ce qu'il peut voir et faire dans le
          dashboard. Choisissez le rôle selon le besoin métier : un comptable a besoin des wallets et
          refunds, un développeur a besoin des logs et des clés API, etc. Seul l'<strong>Admin marchand</strong> peut
          gérer l'équipe.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {ROLES.map((r) => (
          <div key={r.key} className="card">
            <div className="card-body">
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg(r.badge)}`}>
                  {r.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-[#181c32] text-base">{r.name}</div>
                    <Badge color={r.badge}>{r.key}</Badge>
                  </div>
                  <div className="text-sm text-[#7e8299]">{r.short}</div>
                </div>
              </div>
              <p className="text-sm text-[#3f4254] leading-relaxed mb-4">{r.description}</p>
              <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-2">
                Permissions
              </div>
              <ul className="space-y-1.5">
                {r.permissions.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-[#3f4254]">
                    <Check size={14} className="text-success-700 shrink-0 mt-0.5" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Matrice des permissions</div>
            <div className="card-subtitle">Référence rapide des accès par rôle</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f9fafb] border-b-2 border-[#e4e6ef]">
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#3f4254]">
                  Fonctionnalité
                </th>
                {ROLES.map((r) => (
                  <th key={r.key} className="px-4 py-3 text-center text-xs uppercase tracking-wider font-bold text-[#3f4254]">
                    {r.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX_FEATURES.map((f, i) => (
                <tr key={i} className="border-b border-[#eff2f5] hover:bg-[#fcfcfd]">
                  <td className="px-6 py-3 text-sm font-medium text-[#181c32]">{f.label}</td>
                  {ROLES.map((r) => (
                    <td key={r.key} className="px-4 py-3 text-center">
                      {f.allowed[r.key] ? (
                        <Check size={16} className="inline text-success-700" />
                      ) : (
                        <Minus size={16} className="inline text-[#cbd5e0]" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function iconBg(c: Role["badge"]): string {
  return {
    primary: "bg-brand-50 text-brand-700",
    info: "bg-info-50 text-info-700",
    warning: "bg-warning-50 text-warning-700",
    success: "bg-success-50 text-success-700",
  }[c];
}
